import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Lotus Maternal — Meet Queen Brie, Lead Doula Brooklyn NY',
  description: 'Learn about Queen Brie, CEO and Lead Doula at Lotus Maternal. Compassionate, culturally competent doula care for all families through pregnancy, birth, and postpartum.',
  url: `${siteUrl}/about`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'About', item: `${siteUrl}/about` },
    ],
  },
  mainEntity: {
    '@type': 'Person',
    name: 'Queen Brie',
    jobTitle: 'CEO & Lead Doula',
    description: 'Certified doula and founder of Lotus Maternal, providing culturally competent maternal care for all families in Brooklyn, NY.',
    worksFor: {
      '@type': 'MedicalBusiness',
      name: 'Lotus Maternal',
      url: siteUrl,
      telephone: '(929)473-8867',
      email: 'support@lotusmaternal.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Brooklyn',
        addressRegion: 'NY',
        addressCountry: 'US',
      },
    },
    image: 'https://static.readdy.ai/image/0e3c418cd49273f94bb710e1b839df62/01a82f79f111968205c883e51e2f6cc1.png',
    sameAs: ['https://instagram.com/lotus.maternal'],
  },
};

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="About Lotus Maternal — Meet Queen Brie, Lead Doula | Brooklyn, NY"
        description="Meet Queen Brie, CEO and Lead Doula at Lotus Maternal in Brooklyn, NY. Dedicated to compassionate, personalized doula care for all families through every stage of the maternal journey."
        keywords="Queen Brie doula Brooklyn, Lotus Maternal founder, culturally competent maternal care, lead doula New York, family doula support"
        canonical={`${siteUrl}/about`}
        ogTitle="Meet Queen Brie — Lead Doula at Lotus Maternal"
        ogDescription="Compassionate, personalized doula care for all families through every stage of the maternal journey. Based in Brooklyn, NY."
        schema={aboutSchema}
      />
      <Navbar />
      
      {/* Hero Section with Queen Brie */}
      <section className="min-h-screen flex items-center">
        <div className="w-full max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image Side */}
            <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://static.readdy.ai/image/0e3c418cd49273f94bb710e1b839df62/01a82f79f111968205c883e51e2f6cc1.png"
                alt="Queen Brie, CEO of Lotus Maternal"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            </div>

            {/* Content Side */}
            <div className="space-y-8">
              <div>
                <p className="text-emerald-500 font-semibold text-lg mb-2">Meet Our Founder</p>
                <h1 className="font-playfair text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
                  Queen Brie
                </h1>
                <p className="text-xl text-pink-500 font-semibold mb-6">CEO & Lead Doula</p>
              </div>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  Our team of experts is dedicated to helping individuals and couples navigate the often complex journey towards parenthood. We offer personalized guidance and support to help you achieve your goals and optimize your chances of success.
                </p>
                <p className="text-lg">
                  Our fertility services include a thorough evaluation of your reproductive health, as well as recommendations for lifestyle changes and fertility treatments that may be right for you. We also provide emotional support throughout the process, as we understand how challenging it can be.
                </p>
                <p className="text-lg">
                  During pregnancy, we offer comprehensive prenatal care and education to help you prepare for childbirth and ensure the health of both you and your baby. Our birth support services include assistance with creating a birth plan, coaching during labor, and postpartum care for both mother and baby.
                </p>
                <p className="text-lg">
                  Finally, we provide postpartum support to help you navigate the challenges of the early days and weeks of parenthood. This includes lactation support, newborn care education, and emotional support for new parents.
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  At Lotus Maternal, we are committed to providing compassionate, personalized care to support you through every stage of your journey towards parenthood.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-gradient-to-br from-pink-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto space-y-6">
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-gray-900">
              Our Mission
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Lotus Maternal provides doula services designed to support all families throughout their fertility, pregnancy, birth, and postpartum journey.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We believe every family deserves compassionate, informed support during one of life's most transformative experiences. Our goal is to bridge gaps in care by providing culturally competent support and advocacy for every birthing person and their loved ones.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our doulas are trained to provide emotional, physical, and informational support to both the birthing person and their partner or support system. We work to ensure that every family feels empowered, informed, and supported throughout their journey to parenthood.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-center text-gray-900 mb-16">
            Our Core Values
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-heart-3-fill',
                title: 'Compassionate Care',
                description: 'We provide warm, empathetic support tailored to your unique needs and circumstances.'
              },
              {
                icon: 'ri-community-fill',
                title: 'Cultural Competency',
                description: 'We honor and celebrate every family\'s culture, traditions, and experiences in maternal care.'
              },
              {
                icon: 'ri-shield-check-fill',
                title: 'Family Advocacy',
                description: 'We empower you to make informed decisions and advocate for your birth preferences.'
              },
              {
                icon: 'ri-leaf-fill',
                title: 'Holistic Wellness',
                description: 'We support your physical, emotional, and spiritual well-being throughout your journey.'
              }
            ].map((value, index) => (
              <div key={index} className="text-center space-y-4 p-6 rounded-xl hover:bg-pink-50 transition-colors duration-300">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-pink-400 to-emerald-400 rounded-full flex items-center justify-center">
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="font-playfair text-xl font-bold text-gray-900">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { number: '100+', label: 'Families Supported', icon: 'ri-parent-fill' },
              { number: '24/7', label: 'Support Available', icon: 'ri-customer-service-2-fill' },
              { number: '4.9★', label: 'Average Rating', icon: 'ri-star-fill' }
            ].map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-pink-400 to-emerald-400 rounded-full flex items-center justify-center">
                  <i className={`${stat.icon} text-3xl text-white`}></i>
                </div>
                <div className="font-playfair text-5xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-semibold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f93ba2496e5868861f1b556af7a9d4be.jpg"
            alt="Book your consultation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-pink-900/80 to-emerald-900/80"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white space-y-6">
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-xl">
            Book your free 30-minute consultation and discover how we can support you.
          </p>
          <Link
            to="/booking"
            className="inline-block bg-white text-pink-600 px-10 py-4 rounded-full font-semibold text-lg hover:bg-pink-50 transition-colors duration-300 shadow-lg whitespace-nowrap cursor-pointer"
          >
            Book Free Consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;